import { isApiError, unwrap } from "@sagot/api-client";
import { parseSSE, STREAM_RETRY_DELAYS_MS } from "@sagot/chat";
import { API_HEADERS, WALLBOARD_EVENT_NAME, WALLBOARD_POLL_S, WALLBOARD_REFRESH_S } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { getActiveWorkspaceId } from "../../../app/api";
import { useApi, useClient } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { parseSnapshot, type WallboardSnapshot } from "./wallboard-model";

export const wallboardKeys = {
  snapshot: (ws: string) => ["ws", ws, "wallboard"] as const,
};

/** "connecting" until the first snapshot arrives, "live" while snapshots keep coming, "reconnecting" when they stop. */
export type WallboardLink = "connecting" | "live" | "reconnecting";

const POLL_MS = WALLBOARD_POLL_S * 1000;
/** No snapshot for this long on a stream that is open: something buffers it, so the console polls until they flow again. */
const SILENCE_MS = (WALLBOARD_REFRESH_S * 3 + 1) * 1000;
const SILENCE_CHECK_MS = 2000;
/** A stream that ends cleanly sooner than this counts as a failure (backoff), not a rollover. */
const ROLLOVER_MIN_MS = 5000;

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });

/**
 * The wallboard's event stream (`GET /v1/wallboard/events`, SSE over fetch with bearer auth): every snapshot goes to `onSnapshot`.
 * Reconnects with backoff, and at once when the server ends a stream that ran its course (it closes them after ten minutes).
 * With `enabled = false` no connection is made.
 */
function useWallboardStream(enabled: boolean, onSnapshot: (snapshot: WallboardSnapshot) => void): WallboardLink {
  const client = useClient();
  const [link, setLink] = useState<WallboardLink>("connecting");
  const handler = useRef(onSnapshot);
  const lastEventAt = useRef(0);
  useEffect(() => {
    handler.current = onSnapshot;
  }, [onSnapshot]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    const stopped = () => signal.aborted;
    let open = false;
    const silence = setInterval(() => {
      if (open && Date.now() - lastEventAt.current > SILENCE_MS) setLink("reconnecting");
    }, SILENCE_CHECK_MS);

    async function connectOnce(): Promise<number> {
      const token = await client.session.ensureFresh();
      const workspaceId = getActiveWorkspaceId();
      const headers = new Headers({ accept: "text/event-stream" });
      if (token) headers.set(API_HEADERS.authorization, `Bearer ${token.value}`);
      if (workspaceId) headers.set(API_HEADERS.workspace, workspaceId);
      const response = await fetch(`${client.baseUrl}/v1/wallboard/events`, { headers, credentials: "include", signal });
      if (!response.ok || !response.body) throw new Error("wallboard stream unavailable");
      const startedAt = Date.now();
      open = true;
      lastEventAt.current = startedAt;
      for await (const message of parseSSE(response.body, { signal })) {
        if (message.event !== WALLBOARD_EVENT_NAME) continue;
        let raw: unknown;
        try {
          raw = JSON.parse(message.data);
        } catch {
          continue;
        }
        const snapshot = parseSnapshot(raw);
        if (!snapshot) continue;
        lastEventAt.current = Date.now();
        setLink("live");
        handler.current(snapshot);
      }
      return Date.now() - startedAt;
    }

    async function run(): Promise<void> {
      let failures = 0;
      while (!stopped()) {
        try {
          const ranFor = await connectOnce();
          failures = ranFor >= ROLLOVER_MIN_MS ? 0 : failures + 1;
        } catch {
          failures += 1;
        }
        open = false;
        if (stopped()) return;
        setLink("reconnecting");
        const delay = failures === 0 ? 0 : (STREAM_RETRY_DELAYS_MS[Math.min(failures - 1, STREAM_RETRY_DELAYS_MS.length - 1)] ?? 10_000);
        await wait(delay, signal);
      }
    }
    void run();
    return () => {
      clearInterval(silence);
      controller.abort();
    };
  }, [client, enabled]);

  return link;
}

export interface WallboardState {
  snapshot: WallboardSnapshot | undefined;
  link: WallboardLink;
  isPending: boolean;
  /** The last read failed (stale data, if any, stays on the board). */
  isError: boolean;
  error: unknown;
  refetch: () => void;
  /** When the figures on the board were last replaced, in epoch ms (0 = never). */
  updatedAt: number;
}

/**
 * The snapshot: read once with `GET /v1/wallboard`, then replaced by every streamed one. While the stream is not delivering
 * the console polls the same endpoint every `WALLBOARD_POLL_S` seconds, so the board never goes quieter than the promise.
 */
export function useWallboard(enabled: boolean): WallboardState {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  const key = wallboardKeys.snapshot(ws);
  const link = useWallboardStream(enabled, (snapshot) => {
    queryClient.setQueryData(key, snapshot);
  });
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const snapshot = parseSnapshot(await unwrap(api.GET("/v1/wallboard")));
      if (!snapshot) throw new Error("unexpected wallboard response");
      return snapshot;
    },
    enabled,
    retry: 1,
    staleTime: 0,
    refetchInterval: link === "live" ? false : POLL_MS,
  });
  return {
    snapshot: query.data,
    link,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
    updatedAt: query.dataUpdatedAt,
  };
}

export interface ReassignInput {
  conversationId: string;
  toMemberId: string;
}

/** `POST /v1/wallboard/reassign`: moves one chat; the board reads the new figures at once (the stream confirms within seconds). */
export function useReassign() {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, toMemberId }: ReassignInput) =>
      unwrap(api.POST("/v1/wallboard/reassign", { body: { conversation_id: conversationId, to_member_id: toMemberId } })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: wallboardKeys.snapshot(ws) }),
  });
}

/** The reasons a reassign is refused, as the console words them. */
export type ReassignFailure = "limit" | "gone" | "other";

export function reassignFailure(error: unknown): ReassignFailure {
  if (!isApiError(error)) return "other";
  if (error.code === "at_chat_limit") return "limit";
  if (error.status === 404 || error.status === 409) return "gone";
  return "other";
}
